import { Component } from "@theme/component";
import { onAnimationEnd } from "@theme/utilities";
import { ThemeEvents, CartUpdateEvent } from "@theme/events";

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ["cartBubble", "cartBubbleText"];

  /** @type {number} */
  get currentCartCount() {
    // ➜ FIX: default to 0 instead of NaN when the bubble is still empty
    const count = parseInt(this.refs.cartBubbleText.textContent ?? "0", 10);
    return Number.isNaN(count) ? 0 : count;
  }

  set currentCartCount(value) {
    this.refs.cartBubbleText.textContent = value < 100 ? String(value) : "";
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    // Always fetch live cart state after any update
    try {
      const response = await fetch("/cart.js");
      const data = await response.json();
      const itemCount = data.item_count || 0;
      this.renderCartBubble(itemCount, false);
    } catch (_) {
      // fallback if needed
      this.renderCartBubble(0, false);
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (
    itemCount,
    comingFromProductForm,
    animate = true
  ) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count
    this.refs.cartBubble.classList.toggle("visually-hidden", itemCount === 0);
    this.refs.cartBubble.classList.toggle(
      "cart-bubble--animating",
      itemCount > 0 && animate
    );
    this.currentCartCount = itemCount;

    this.classList.toggle("header-actions__cart-icon--has-cart", itemCount > 0);

    sessionStorage.setItem(
      "cart-count",
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    await onAnimationEnd(this.refs.cartBubble);

    this.refs.cartBubble.classList.remove("cart-bubble--animating");
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = async () => {
    try {
      const response = await fetch("/cart.js");
      const data = await response.json();
      const count = data.item_count || 0;
      this.renderCartBubble(count, false, false);
      sessionStorage.setItem(
        "cart-count",
        JSON.stringify({
          value: String(count),
          timestamp: Date.now(),
        })
      );
    } catch (_) {
      this.renderCartBubble(0, false, false);
    }
  };
}

if (!customElements.get("cart-icon")) {
  customElements.define("cart-icon", CartIcon);
}
